/**
 * Vue i18n Configuration
 * Internationalization plugin setup for multi-language support
 * Uses modular structure for better maintainability
 */

import { createI18n } from 'vue-i18n'
import logger from '@/services/logger'

// URL locale to i18n locale mapping
const URL_TO_I18N = {
  'en': 'en',
  'pt-br': 'pt',
  'es': 'es'
}

// i18n locale to URL locale mapping
const I18N_TO_URL = {
  'en': 'en',
  'pt': 'pt-br',
  'es': 'es'
}

// Valid URL locales
export const VALID_URL_LOCALES = ['en', 'pt-br', 'es']

/**
 * Convert URL locale code to i18n locale code
 * @param {string} urlLocale - URL locale (en, pt-br, es)
 * @returns {string} i18n locale (en, pt, es)
 */
export function urlLocaleToI18n(urlLocale) {
  return URL_TO_I18N[urlLocale] || 'en'
}

/**
 * Convert i18n locale code to URL locale code
 * @param {string} i18nLocale - i18n locale (en, pt, es)
 * @returns {string} URL locale (en, pt-br, es)
 */
export function i18nLocaleToUrl(i18nLocale) {
  return I18N_TO_URL[i18nLocale] || 'en'
}

/**
 * Extract locale from URL path
 * @param {string} path - URL path
 * @returns {string|null} URL locale or null if not found
 */
export function getLocaleFromPath(path) {
  const match = path.match(/^\/(en|pt-br|es)(?:\/|$)/)
  return match ? match[1] : null
}

/**
 * Check if a URL locale is valid
 * @param {string} locale - URL locale to check
 * @returns {boolean}
 */
export function isValidUrlLocale(locale) {
  return VALID_URL_LOCALES.includes(locale)
}

// Import English translations (modular)
import enCommon from '@/locales/en/common.json'
import enAuth from '@/locales/en/auth.json'
import enNavigation from '@/locales/en/navigation.json'
import enErrors from '@/locales/en/errors.json'
import enDashboard from '@/locales/en/pages/dashboard.json'
import enCampaigns from '@/locales/en/pages/campaigns.json'
import enContacts from '@/locales/en/pages/contacts.json'
import enTemplates from '@/locales/en/pages/templates.json'
import enAutomations from '@/locales/en/pages/automations.json'
import enAnalytics from '@/locales/en/pages/analytics.json'
import enSettings from '@/locales/en/pages/settings.json'
import enBilling from '@/locales/en/pages/billing.json'
import enPlans from '@/locales/en/pages/plans.json'
import enCredentials from '@/locales/en/pages/credentials.json'
import enSidebar from '@/locales/en/components/sidebar.json'
import enWelcome from '@/locales/en/pages/welcome.json'
import enProfile from '@/locales/en/pages/profile.json'
import enApiDocs from '@/locales/en/pages/api-docs.json'
import enAdmin from '@/locales/en/pages/admin.json'

// Import Portuguese translations (modular)
import ptCommon from '@/locales/pt/common.json'
import ptAuth from '@/locales/pt/auth.json'
import ptNavigation from '@/locales/pt/navigation.json'
import ptErrors from '@/locales/pt/errors.json'
import ptDashboard from '@/locales/pt/pages/dashboard.json'
import ptCampaigns from '@/locales/pt/pages/campaigns.json'
import ptContacts from '@/locales/pt/pages/contacts.json'
import ptTemplates from '@/locales/pt/pages/templates.json'
import ptAutomations from '@/locales/pt/pages/automations.json'
import ptAnalytics from '@/locales/pt/pages/analytics.json'
import ptSettings from '@/locales/pt/pages/settings.json'
import ptBilling from '@/locales/pt/pages/billing.json'
import ptPlans from '@/locales/pt/pages/plans.json'
import ptCredentials from '@/locales/pt/pages/credentials.json'
import ptSidebar from '@/locales/pt/components/sidebar.json'
import ptWelcome from '@/locales/pt/pages/welcome.json'
import ptProfile from '@/locales/pt/pages/profile.json'
import ptApiDocs from '@/locales/pt/pages/api-docs.json'
import ptAdmin from '@/locales/pt/pages/admin.json'

// Import Spanish translations (modular)
import esCommon from '@/locales/es/common.json'
import esAuth from '@/locales/es/auth.json'
import esNavigation from '@/locales/es/navigation.json'
import esErrors from '@/locales/es/errors.json'
import esDashboard from '@/locales/es/pages/dashboard.json'
import esCampaigns from '@/locales/es/pages/campaigns.json'
import esContacts from '@/locales/es/pages/contacts.json'
import esTemplates from '@/locales/es/pages/templates.json'
import esAutomations from '@/locales/es/pages/automations.json'
import esAnalytics from '@/locales/es/pages/analytics.json'
import esSettings from '@/locales/es/pages/settings.json'
import esBilling from '@/locales/es/pages/billing.json'
import esPlans from '@/locales/es/pages/plans.json'
import esCredentials from '@/locales/es/pages/credentials.json'
import esSidebar from '@/locales/es/components/sidebar.json'
import esWelcome from '@/locales/es/pages/welcome.json'
import esProfile from '@/locales/es/pages/profile.json'
import esApiDocs from '@/locales/es/pages/api-docs.json'
import esAdmin from '@/locales/es/pages/admin.json'

// Merge modular translations into complete language objects
const en = {
  common: enCommon,
  auth: enAuth,
  navigation: enNavigation,
  errors: enErrors,
  sidebar: enSidebar,
  dashboard: enDashboard,
  campaigns: enCampaigns,
  contacts: enContacts,
  templates: enTemplates,
  automations: enAutomations,
  analytics: enAnalytics,
  settings: enSettings,
  billing: enBilling,
  plans: enPlans,
  credentials: enCredentials,
  welcome: enWelcome,
  profile: enProfile,
  apiDocs: enApiDocs,
  admin: enAdmin
}

const pt = {
  common: ptCommon,
  auth: ptAuth,
  navigation: ptNavigation,
  errors: ptErrors,
  sidebar: ptSidebar,
  dashboard: ptDashboard,
  campaigns: ptCampaigns,
  contacts: ptContacts,
  templates: ptTemplates,
  automations: ptAutomations,
  analytics: ptAnalytics,
  settings: ptSettings,
  billing: ptBilling,
  plans: ptPlans,
  credentials: ptCredentials,
  welcome: ptWelcome,
  profile: ptProfile,
  apiDocs: ptApiDocs,
  admin: ptAdmin
}

const es = {
  common: esCommon,
  auth: esAuth,
  navigation: esNavigation,
  errors: esErrors,
  sidebar: esSidebar,
  dashboard: esDashboard,
  campaigns: esCampaigns,
  contacts: esContacts,
  templates: esTemplates,
  automations: esAutomations,
  analytics: esAnalytics,
  settings: esSettings,
  billing: esBilling,
  plans: esPlans,
  credentials: esCredentials,
  welcome: esWelcome,
  profile: esProfile,
  apiDocs: esApiDocs,
  admin: esAdmin
}

/**
 * Detect browser language and map to supported locale
 * Handles various browser language formats: en, en-US, pt, pt-BR, es, es-ES, etc.
 */
function detectBrowserLocale() {
  // Try navigator.language first, then navigator.languages array
  const languages = navigator.languages || [navigator.language]

  for (const lang of languages) {
    if (!lang) continue

    const langLower = lang.toLowerCase()

    // Direct match for supported locales
    if (langLower === 'pt-br' || langLower === 'pt_br') return 'pt'
    if (langLower === 'pt') return 'pt'
    if (langLower.startsWith('pt-') || langLower.startsWith('pt_')) return 'pt'

    if (langLower === 'es' || langLower.startsWith('es-') || langLower.startsWith('es_')) return 'es'

    if (langLower === 'en' || langLower.startsWith('en-') || langLower.startsWith('en_')) return 'en'
  }

  return null
}

// Default locale detection
function detectLocale() {
  // Check URL path first (highest priority for SEO)
  const urlLocale = getLocaleFromPath(window.location.pathname)
  if (urlLocale) {
    const i18nLocale = urlLocaleToI18n(urlLocale)
    // Also update localStorage to sync
    localStorage.setItem('user_locale', i18nLocale)
    return i18nLocale
  }

  // Check localStorage (only if user has explicitly set a preference before)
  const stored = localStorage.getItem('user_locale')
  if (stored && isValidLocale(stored)) {
    return stored
  }

  // Detect browser language (for new users)
  const browserLocale = detectBrowserLocale()
  if (browserLocale) {
    // Save detected locale so it's used consistently
    localStorage.setItem('user_locale', browserLocale)
    return browserLocale
  }

  // Fallback to English
  return 'en'
}

function isValidLocale(locale) {
  return ['en', 'pt', 'es'].includes(locale)
}

// Create i18n instance
const i18n = createI18n({
  legacy: false, // Use Composition API
  locale: detectLocale(),
  fallbackLocale: 'en',
  globalInjection: true,
  messages: {
    en,
    pt,
    es
  },
  numberFormats: {
    en: {
      currency: {
        style: 'currency',
        currency: 'USD',
        notation: 'standard'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        useGrouping: false
      }
    },
    pt: {
      currency: {
        style: 'currency',
        currency: 'BRL',
        notation: 'standard'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        useGrouping: false
      }
    },
    es: {
      currency: {
        style: 'currency',
        currency: 'EUR',
        notation: 'standard'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        useGrouping: false
      }
    }
  },
  datetimeFormats: {
    en: {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric'
      }
    },
    pt: {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric'
      }
    },
    es: {
      short: {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      },
      long: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric'
      }
    }
  }
})

// Language utility functions
export const locales = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' }
]

export function setLocale(locale) {
  if (isValidLocale(locale)) {
    i18n.global.locale.value = locale
    localStorage.setItem('user_locale', locale)
    document.documentElement.lang = locale
    
    logger.logUserAction('Language Changed', { locale })
    
    // Update axios headers for API requests
    if (window.axios) {
      window.axios.defaults.headers.common['Accept-Language'] = locale
    }
    
    return true
  }
  return false
}

export function getCurrentLocale() {
  return i18n.global.locale.value
}

export function getAvailableLocales() {
  return locales
}

// Initialize locale on startup
document.documentElement.lang = getCurrentLocale()
if (window.axios) {
  window.axios.defaults.headers.common['Accept-Language'] = getCurrentLocale()
}

export default i18n